import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react'

export type View = 'local' | 'tourism'

const STORAGE_KEY = 'medairbnb.view'
const DEFAULT_VIEW: View = 'local'

type ViewContextValue = {
  view: View
  setView: (next: View) => void
}

const ViewContext = createContext<ViewContextValue | undefined>(undefined)

function isView(value: unknown): value is View {
  return value === 'local' || value === 'tourism'
}

function readStoredView(): View {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (isView(raw)) return raw
  } catch {
    // localStorage unavailable (privacy mode, SSR, or disabled cookies) —
    // fall through to the in-memory default.
  }
  return DEFAULT_VIEW
}

function persistView(value: View): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, value)
  } catch {
    // Ignore — in-memory state still updates so the toggle remains usable.
  }
}

export function ViewProvider({ children }: { children: ReactNode }) {
  const [view, setViewState] = useState<View>(readStoredView)

  const setView = useCallback((next: View) => {
    persistView(next)
    setViewState(next)
  }, [])

  return (
    <ViewContext.Provider value={{ view, setView }}>
      {children}
    </ViewContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- provider + hook colocated by convention
export function useView(): ViewContextValue {
  const ctx = useContext(ViewContext)
  if (!ctx) {
    throw new Error('useView must be used inside <ViewProvider>')
  }
  return ctx
}
